"use client";

import { useCallback, useEffect, useId, useRef, useState, type CSSProperties } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Monogram from "./Monogram";
import CartLink from "./CartLink";
import MobileNav from "./MobileNav";
import { sectionForPath } from "../../lib/nav";
import type { MenuImage, MenuLink, MenuPanel, SiteMenu } from "../../lib/menu";

const OPEN_DELAY = 80;
const CLOSE_DELAY = 200;

const stagger = (i: number) => ({ "--i": i }) as CSSProperties;

/**
 * Desktop header + mega menu (md and up) and the mobile drawer trigger.
 *
 * One full-width paper sheet drops under the bar. Every panel stays mounted
 * and crossfades, so switching MEN -> BRANDS never re-collapses the sheet.
 * Hover or focus on a left-hand link swaps the feature image on the right.
 */
export default function MegaMenu({ menu }: { menu: SiteMenu }) {
  const pathname = usePathname();
  const uid = useId();
  const headerRef = useRef<HTMLElement>(null);
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const subRefs = useRef<Record<string, HTMLElement | null>>({});
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverOpened = useRef(false);
  const pinned = useRef(false);
  const preloaded = useRef<Set<string>>(new Set());

  const [open, setOpen] = useState<string | null>(null);
  const [hover, setHover] = useState<Record<string, string>>({});
  const [focusReq, setFocusReq] = useState<string | null>(null);

  // Close on route change (adjust state during render, not in an effect).
  const [seenPath, setSeenPath] = useState(pathname);
  if (seenPath !== pathname) {
    setSeenPath(pathname);
    setOpen(null);
  }
  useEffect(() => {
    pinned.current = false;
    hoverOpened.current = false;
  }, [pathname]);

  const panels = menu.panels;
  const activeKey = open ?? sectionForPath(pathname);

  const clearTimers = useCallback(() => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  }, []);

  const preload = useCallback((panel: MenuPanel) => {
    if (preloaded.current.has(panel.key)) return;
    preloaded.current.add(panel.key);
    const urls = new Set<string>();
    panel.feature.images.forEach((i) => urls.add(i.src));
    const walk = (l: MenuLink) => {
      l.images?.forEach((i) => urls.add(i.src));
      l.children?.forEach(walk);
    };
    panel.links.forEach(walk);
    urls.forEach((src) => {
      const img = new Image();
      img.decoding = "async";
      img.src = src;
    });
  }, []);

  // Warm the FIRST panel's feature images once the browser is idle so the first
  // open never pops in. The other panels warm on pointer-enter/open (preload).
  // Desktop only: phones use the drawer and never see these images.
  useEffect(() => {
    if (!window.matchMedia("(min-width: 768px)").matches) return;
    const run = () =>
      panels[0]?.feature.images.forEach((i) => {
        const img = new Image();
        img.decoding = "async";
        img.src = i.src;
      });
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(run, { timeout: 2500 });
      return () => w.cancelIdleCallback?.(id);
    }
    const t = setTimeout(run, 1500);
    return () => clearTimeout(t);
  }, [panels]);

  const openPanel = useCallback(
    (key: string, byHover: boolean) => {
      const panel = panels.find((p) => p.key === key);
      if (!panel) return;
      setOpen((cur) => {
        if (cur !== key) setHover((h) => ({ ...h, [key]: "" }));
        return key;
      });
      hoverOpened.current = byHover;
      if (!byHover) pinned.current = true;
      preload(panel);
    },
    [panels, preload],
  );

  const closePanel = useCallback(() => {
    clearTimers();
    pinned.current = false;
    hoverOpened.current = false;
    setOpen(null);
  }, [clearTimers]);

  const scheduleClose = useCallback(() => {
    if (pinned.current) return;
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      hoverOpened.current = false;
      setOpen(null);
    }, CLOSE_DELAY);
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  // Keyboard open: move focus to the first link of the freshly opened panel.
  useEffect(() => {
    if (!focusReq || open !== focusReq) return;
    const id = requestAnimationFrame(() => {
      subRefs.current[focusReq]?.querySelector<HTMLElement>("a[href]")?.focus({ preventScroll: true });
      setFocusReq(null);
    });
    return () => cancelAnimationFrame(id);
  }, [focusReq, open]);

  function onTriggerPointerEnter(e: React.PointerEvent, key: string) {
    if (e.pointerType !== "mouse") return;
    if (closeTimer.current) clearTimeout(closeTimer.current);
    if (openTimer.current) clearTimeout(openTimer.current);
    if (open) {
      if (open !== key) openPanel(key, !pinned.current);
      return;
    }
    openTimer.current = setTimeout(() => openPanel(key, true), OPEN_DELAY);
  }

  function onTriggerClick(e: React.MouseEvent, key: string) {
    clearTimers();
    const viaKeyboard = e.detail === 0;
    if (viaKeyboard) {
      if (open !== key) openPanel(key, false);
      setFocusReq(key);
      return;
    }
    if (open === key) {
      if (hoverOpened.current) {
        // Opened by hover: a click pins it open instead of closing it.
        hoverOpened.current = false;
        pinned.current = true;
      } else {
        closePanel();
      }
      return;
    }
    openPanel(key, false);
  }

  function onTriggerKeyDown(e: React.KeyboardEvent, key: string) {
    const keys = panels.map((p) => p.key);
    const i = keys.indexOf(key);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (open !== key) openPanel(key, false);
      setFocusReq(key);
    } else if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      const next = keys[(i + (e.key === "ArrowRight" ? 1 : -1) + keys.length) % keys.length];
      triggerRefs.current[next]?.focus();
      if (open) openPanel(next, false);
    }
  }

  // While a panel is open: Esc closes it wherever focus is (WCAG 1.4.13), and
  // scrolling the page more than 40px away from where it opened dismisses it.
  useEffect(() => {
    if (!open) return;
    const key = open;
    const startY = window.scrollY;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const inside = headerRef.current?.contains(document.activeElement);
      closePanel();
      if (inside) triggerRefs.current[key]?.focus();
    };
    const onScroll = () => {
      if (Math.abs(window.scrollY - startY) > 40) closePanel();
    };
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll);
    };
  }, [open, closePanel]);

  // A link activated inside the panel closes it even if the route doesn't change.
  function onPanelClick(e: React.MouseEvent) {
    if ((e.target as Element).closest("a")) closePanel();
  }

  function onHeaderBlur(e: React.FocusEvent) {
    const next = e.relatedTarget as Node | null;
    if (open && next && !headerRef.current?.contains(next)) closePanel();
  }

  function onPanelKeyDown(e: React.KeyboardEvent) {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const sub = open ? subRefs.current[open] : null;
    if (!sub) return;
    const items = Array.from(
      sub.querySelectorAll<HTMLElement>(".mm-list a[href], .mm-list button"),
    ).filter((el) => !el.closest("[inert]"));
    const i = items.indexOf(document.activeElement as HTMLElement);
    if (i === -1) return;
    e.preventDefault();
    const n = e.key === "ArrowDown" ? Math.min(i + 1, items.length - 1) : Math.max(i - 1, 0);
    items[n]?.focus();
  }

  return (
    <header
      ref={headerRef}
      className="mm-header absolute inset-x-0 top-0 z-50"
      data-open={open !== null}
      onBlur={onHeaderBlur}
    >
      <button
        type="button"
        tabIndex={-1}
        aria-label="Close menu"
        className="mm-scrim"
        data-open={open !== null}
        onClick={closePanel}
      />

      <div className="mm-bar grid h-16 grid-cols-[1fr_auto_1fr] items-center px-4 md:px-8">
        <div className="flex h-full items-center">
          <MobileNav menu={menu} />

          <nav aria-label="Primary" className="hidden h-full items-stretch md:flex">
            {panels.map((p) => (
              <button
                key={p.key}
                ref={(el) => {
                  triggerRefs.current[p.key] = el;
                }}
                type="button"
                className="mm-trigger"
                data-active={activeKey === p.key}
                aria-expanded={open === p.key}
                aria-controls={`${uid}-${p.key}`}
                onPointerEnter={(e) => onTriggerPointerEnter(e, p.key)}
                onPointerLeave={(e) => e.pointerType === "mouse" && scheduleClose()}
                onClick={(e) => onTriggerClick(e, p.key)}
                onKeyDown={(e) => onTriggerKeyDown(e, p.key)}
              >
                <span className="mm-label">{p.label}</span>
              </button>
            ))}
            {menu.plain?.map((l) => (
              <Link key={l.href} href={l.href} className="mm-trigger">
                <span className="mm-label">{l.label}</span>
              </Link>
            ))}
          </nav>

          <div
            className="mm-panel"
            data-open={open !== null}
            inert={open === null}
            onPointerEnter={(e) => {
              if (e.pointerType === "mouse" && closeTimer.current) clearTimeout(closeTimer.current);
            }}
            onPointerLeave={(e) => e.pointerType === "mouse" && scheduleClose()}
            onKeyDown={onPanelKeyDown}
            onClick={onPanelClick}
          >
            {panels.map((p) => (
              <PanelView
                key={p.key}
                id={`${uid}-${p.key}`}
                panel={p}
                active={open === p.key}
                hoverKey={hover[p.key] ?? ""}
                utility={menu.utility}
                onHover={(k) => setHover((h) => (h[p.key] === k ? h : { ...h, [p.key]: k }))}
                innerRef={(el) => {
                  subRefs.current[p.key] = el;
                }}
              />
            ))}
          </div>
        </div>

        <Link href="/" aria-label="Muñeca's Couture home" className="mm-logo-link">
          <Monogram className="mm-logo h-7 w-auto" />
        </Link>

        <div className="flex items-center justify-end">
          <CartLink />
        </div>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------

interface Slide {
  key: string;
  href: string;
  title: string;
  images: MenuImage[];
}

const FEATURE_KEY = "__feature";

function PanelView({
  id,
  panel,
  active,
  hoverKey,
  utility,
  onHover,
  innerRef,
}: {
  id: string;
  panel: MenuPanel;
  active: boolean;
  hoverKey: string;
  utility: SiteMenu["utility"];
  onHover: (key: string) => void;
  innerRef: (el: HTMLElement | null) => void;
}) {
  const [openAcc, setOpenAcc] = useState<string | null>(null);
  const [wasActive, setWasActive] = useState(active);
  if (wasActive !== active) {
    setWasActive(active);
    if (active) setOpenAcc(null);
  }

  // Slides that have been shown at least once stay mounted, so the previous
  // image is always underneath while the next one fades in.
  const [seen, setSeen] = useState<string[]>([]);
  const current = hoverKey || FEATURE_KEY;
  if (current !== FEATURE_KEY && !seen.includes(current)) setSeen([...seen, current]);

  const slideFor = (l: MenuLink): Slide | null =>
    l.images?.length ? { key: l.href, href: l.href, title: l.caption ?? l.label, images: l.images } : null;
  const all: Slide[] = [{ key: FEATURE_KEY, href: panel.feature.href, title: panel.feature.title, images: panel.feature.images }];
  const walk = (l: MenuLink) => {
    const s = slideFor(l);
    if (s && !all.some((a) => a.key === s.key)) all.push(s);
    l.children?.forEach(walk);
  };
  panel.links.forEach(walk);

  const shown = all.find((s) => s.key === current) ?? all[0];
  const mounted = all.filter((s) => s.key === FEATURE_KEY || s.key === current || seen.includes(s.key));

  const itemProps = (l: MenuLink) => ({
    onMouseEnter: () => l.images?.length && onHover(l.href),
    onFocus: () => l.images?.length && onHover(l.href),
  });

  return (
    <section
      id={id}
      ref={innerRef}
      aria-label={`${panel.heading} menu`}
      className="mm-sub"
      data-active={active}
      inert={!active}
    >
      <div className="mm-grid">
        <div className="mm-left">
          <h2 className="mm-heading mm-item" style={stagger(0)}>
            {panel.heading}
          </h2>
          <ul className="mm-list">
            {panel.links.map((l, i) =>
              l.children ? (
                <li key={l.href + l.label} className="mm-item" style={stagger(i + 1)}>
                  <button
                    type="button"
                    className="mm-link mm-acc-btn"
                    aria-expanded={openAcc === l.label}
                    aria-controls={`${id}-acc-${i}`}
                    onClick={() => setOpenAcc((v) => (v === l.label ? null : l.label))}
                    {...itemProps(l)}
                  >
                    {l.label}
                    <svg className="mm-chev" viewBox="0 0 10 6" width="9" height="6" aria-hidden="true">
                      <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.2" />
                    </svg>
                  </button>
                  <div id={`${id}-acc-${i}`} className="mm-acc" data-open={openAcc === l.label} inert={openAcc !== l.label}>
                    <div>
                      <ul className="mm-sublist">
                        {l.children.map((c) => (
                          <li key={c.href + c.label}>
                            <Link href={c.href} className="mm-link mm-link-sub" {...itemProps(c)}>
                              {c.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </li>
              ) : (
                <li key={l.href + l.label} className="mm-item" style={stagger(i + 1)}>
                  <Link href={l.href} className="mm-link" {...itemProps(l)}>
                    {l.label}
                  </Link>
                </li>
              ),
            )}
          </ul>
          <ul className="mm-utility mm-item" style={stagger(panel.links.length + 2)}>
            {utility.map((u) => (
              <li key={u.href}>
                <Link href={u.href} className="mm-link">
                  {u.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <Link
          href={shown.href}
          className="mm-feature"
          aria-label={`${shown.title}. Explore the collection`}
          tabIndex={active ? 0 : -1}
        >
          {mounted.map((s) => (
            <FeatureSlide key={s.key} slide={s} active={s.key === shown.key} />
          ))}
        </Link>
      </div>
    </section>
  );
}

function FeatureSlide({ slide, active }: { slide: Slide; active: boolean }) {
  const fit = slide.images[0]?.fit ?? "cover";
  return (
    <div className="mm-slide" data-active={active} data-fit={fit} aria-hidden="true">
      {slide.images.length > 1 ? (
        <div className="mm-diptych">
          {slide.images.map((img) => (
            <SlideImg key={img.src} img={img} />
          ))}
        </div>
      ) : slide.images[0] ? (
        <SlideImg img={slide.images[0]} />
      ) : null}
      <p className="mm-cap">
        <b>{slide.title}</b>
        <span>Explore the collection</span>
      </p>
    </div>
  );
}

function SlideImg({ img }: { img: MenuImage }) {
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={img.src}
      alt=""
      loading="lazy"
      decoding="async"
      draggable={false}
      style={{ objectFit: img.fit, objectPosition: img.position }}
    />
  );
}
